import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ChevronRight, GraduationCap, Sparkles, Target } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { niveisEscolares, objetivosEstudo } from '../constants/profileOptions'

const steps = [
  {
    title: 'Bem-vindo ao Agente de Estudo!',
    description: 'Vamos concluir seu perfil em poucos passos para organizar melhor seus estudos.',
    icon: Sparkles
  },
  {
    title: 'Qual seu nível de escolaridade?',
    description: 'Confirme ou altere a opção informada no cadastro.',
    icon: GraduationCap
  },
  {
    title: 'Qual seu principal objetivo?',
    description: 'Essa informação ficará salva no seu perfil e poderá ser alterada depois.',
    icon: Target
  }
]

function OnboardingPage() {
  const navigate = useNavigate()
  const { usuario, accessToken, atualizarUsuario } = useAuthStore()
  const [step, setStep] = useState(0)
  const [dados, setDados] = useState({
    nivel: usuario?.nivel_escolar || '',
    objetivo: usuario?.objetivo || ''
  })
  const [salvando, setSalvando] = useState(false)

  if (!usuario || !accessToken) return <Navigate to="/login?next=/onboarding" replace />

  const currentStep = steps[step]
  const Icon = currentStep.icon
  const canProceed = step === 1 ? Boolean(dados.nivel) : step === 2 ? Boolean(dados.objetivo) : true

  const handleNext = async () => {
    if (step < steps.length - 1) {
      setStep(current => current + 1)
      return
    }

    setSalvando(true)
    try {
      const preferencias = { ...(usuario.preferencias || {}), onboarding_concluido: true }
      const { data } = await api.put('/users/me', {
        nivel_escolar: dados.nivel,
        objetivo: dados.objetivo,
        preferencias
      })
      atualizarUsuario(data.usuario)
      toast.success('Perfil configurado. Bons estudos!')
      navigate('/', { replace: true })
    } catch (error) {
      toast.error(getApiError(error, 'Não foi possível salvar seu perfil.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-gray-900 transition-colors dark:bg-zinc-950 dark:text-zinc-100">
      <div className="fixed inset-x-0 top-0 h-1 bg-gray-100 dark:bg-zinc-800">
        <div className="h-full bg-black transition-all dark:bg-white" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>

      <div className="w-full max-w-sm">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-zinc-800">
          <Icon size={28} className="text-gray-700 dark:text-zinc-200" />
        </div>

        <h1 className="mb-2 text-center text-xl font-medium">{currentStep.title}</h1>
        <p className="mb-8 text-center text-sm text-gray-500 dark:text-zinc-400">{currentStep.description}</p>

        {step === 0 && (
          <div className="text-center">
            <img
              src="/brand/agente-de-estudo-icone.svg"
              alt="Agente de Estudo"
              width="96"
              height="96"
              className="mx-auto mb-6 h-24 w-24"
            />
            <p className="text-sm leading-6 text-gray-500 dark:text-zinc-400">Encontre videoaulas, registre seus aprendizados no caderno e mantenha suas revisões em dia.</p>
          </div>
        )}

        {step === 1 && (
          <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
            {niveisEscolares.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setDados(current => ({ ...current, nivel: item.value }))}
                className={`w-full rounded-xl border p-3 text-left transition-all ${dados.nivel === item.value ? 'border-black bg-gray-50 dark:border-white dark:bg-zinc-800' : 'border-gray-100 hover:border-gray-300 dark:border-zinc-800 dark:hover:border-zinc-600'}`}
              >
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-gray-500 dark:text-zinc-400">{item.desc}</p>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
            {objetivosEstudo.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setDados(current => ({ ...current, objetivo: item.value }))}
                className={`w-full rounded-xl border p-3 text-left transition-all ${dados.objetivo === item.value ? 'border-black bg-gray-50 dark:border-white dark:bg-zinc-800' : 'border-gray-100 hover:border-gray-300 dark:border-zinc-800 dark:hover:border-zinc-600'}`}
              >
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-gray-500 dark:text-zinc-400">{item.desc}</p>
              </button>
            ))}
          </div>
        )}

        <button type="button" onClick={handleNext} disabled={!canProceed || salvando} className="btn-primary mt-8 flex w-full items-center justify-center gap-2 py-3">
          {salvando ? 'Salvando...' : step === steps.length - 1 ? 'Começar a estudar!' : 'Próximo'}
          {!salvando && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  )
}

export default OnboardingPage
